(() => {
  if (customElements.get('product-discovery')) return;
  const historyKey = 'coweet-product-history';
  function readHistory(storage, days, current, now = Date.now()) {
    try {
      const rows = JSON.parse(storage.getItem(historyKey) || '[]');
      return Array.isArray(rows) ? rows.filter(item => item && /^[a-z0-9-]+$/.test(item.handle) && Number.isFinite(item.at) && item.at <= now && now - item.at < days * 86400000 && item.handle !== current).filter((item, i, all) => all.findIndex(other => other.handle === item.handle) === i).slice(0, 20) : [];
    } catch { return []; }
  }
  class ProductDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      this.limit = Math.max(2, Math.min(10, Number(this.dataset.limit) || 8));
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.handle = this.dataset.productHandle;
      this.requests = new Map();
      let history = [];
      try {
        history = readHistory(localStorage, Number(this.dataset.recentDays) || 30, this.handle);
        if (this.handle) localStorage.setItem(historyKey, JSON.stringify([{handle:this.handle, at:Date.now()}, ...history].slice(0,20)));
      } catch { /* Browsing and recommendations still work with storage blocked. */ }
      this.recentHandles = this.querySelector('[data-mode="recently_viewed"]') ? history.map(item => item.handle) : [];
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-scroll]');
        if (!button) return;
        const row = button.closest('[data-mode]').querySelector('ul');
        row.scrollBy({left:Number(button.dataset.scroll) * row.clientWidth});
      });
      this.load('new_arrival', () => this.collection('created-descending'));
      this.load('best_selling', () => this.collection('best-selling'));
      this.load('recently_viewed', () => this.products(history.map(item => item.handle)));
      this.load('recommended', () => [...(this.querySelector('[data-curated-cards]')?.content.querySelectorAll('[data-handle]') || [])]);
    }
    async cards(url, selector) {
      const response = await fetch(url, {signal:AbortSignal.timeout(10000)});
      if (!response.ok) throw new Error('Product discovery request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return [...(doc.querySelector(selector)?.content.querySelectorAll('[data-handle]') || [])];
    }
    async products(handles) {
      const valid = [...new Set(handles)].filter(handle => typeof handle === 'string' && /^[a-z0-9-]+$/.test(handle) && handle !== this.handle).slice(0, this.limit);
      const results = await Promise.allSettled(valid.map(handle => {
        if (!this.requests.has(handle)) {
          const url = new URL(this.root + 'products/' + handle, location.origin);
          url.searchParams.set('section_id', 'coweet-product-discovery');
          this.requests.set(handle, this.cards(url, '[data-current-card]'));
        }
        return this.requests.get(handle);
      }));
      return results.flatMap(result => result.status === 'fulfilled' ? result.value : []);
    }
    collection(sort) {
      const handle = this.dataset.collection || 'all';
      const url = new URL(this.root + 'collections/' + encodeURIComponent(handle), location.origin);
      url.searchParams.set('sort_by', sort);
      url.searchParams.set('section_id', 'coweet-product-discovery');
      return this.cards(url, '[data-collection-cards]');
    }
    async load(mode, source) {
      const group = this.querySelector(`[data-mode="${mode}"]`);
      if (!group) return;
      try {
        const seen = new Set([this.handle, ...(mode === 'recommended' ? this.recentHandles : [])]);
        const cards = (await source()).filter(card => {
          const handle = card.dataset.handle;
          if (!handle || seen.has(handle)) return false;
          seen.add(handle); return true;
        }).slice(0, this.limit).map(card => card.cloneNode(true));
        if (!this.isConnected) return;
        for (const card of cards) {
          const suffix = '-' + this.dataset.section + '-' + mode;
          const ids = new Map([...card.querySelectorAll('[id]')].map(node => [node.id, node.id + suffix]));
          for (const node of card.querySelectorAll('[id]')) node.id = ids.get(node.id);
          for (const node of card.querySelectorAll('[aria-labelledby],[aria-describedby],[for]')) {
            for (const attr of ['aria-labelledby','aria-describedby','for']) {
              if (node.hasAttribute(attr)) node.setAttribute(attr, node.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' '));
            }
          }
        }
        group.querySelector('ul').replaceChildren(...cards);
        group.hidden = cards.length === 0;
      } catch { group.hidden = true; }
    }
  }
  customElements.define('product-discovery', ProductDiscovery);
})();
